import React, { CSSProperties, useRef, useState } from 'react'
import cls from 'classnames'
import styles from './popover.module.scss';
import { PopoverHeaderProps } from './Header';
import PopoverBody from './Body';
import { getUniqueId } from '../../../utils';

export interface BasicPopoverProps {
  open?: boolean;
  onClose?: () => void;
  onHidden?: () => void;
}

interface Props extends BasicPopoverProps, Pick<PopoverHeaderProps, 'title'> {
  children?: any;

  /**
   * If provided, this element may be clicked without hiding the popover.
   */
  includeClickableElement?: HTMLElement;

  /**
   * Whether to hide the popover by clicking outside of it,
   */
  dismissOutside?: boolean;

  closeButton?: boolean;
  horizontalPosition?: 'left' | 'center' | 'right';
  verticalPosition?: 'top' | 'bottom';

  className?: string;

  /**
   * Whether to wrap `children` in a PopoverBody.
   * Pass `false` if you are rendering your own popover bodies.
   * 
   * @default true
   */
  wrapChildren?: boolean;

  style?: CSSProperties;
}

var popoverId = 0
//  current dismissable listener
var dismissableListener: any;
var openRef: { current?: HTMLDivElement } = {};
var preventDismissFlag = false;
var activePopover: {
  id: string;
  onClose: () => void;
} = null;

/**
 * Basic presentable popover which wraps some children.
 * The anchor point of the popover is at the tip of the notch.
 */
export default function Popover({
  children,
  className,
  closeButton = true,
  dismissOutside = true,
  horizontalPosition = 'center',
  includeClickableElement,
  onClose,
  onHidden,
  open,
  title,
  style,
  verticalPosition = 'bottom',
  wrapChildren = true
}: Props) {
  const [hidden, setHidden] = useState(!open);

  const id = useRef(getUniqueId('popover'));
  const containerRef = useRef<HTMLDivElement>(null);

  const firedOnHidden = useRef(false);

  /**
   * @returns Whether the provided mouse event occurred inside this element. 
   */
  function isInsideThisElement(e: MouseEvent) {
    const elementPath = e.composedPath() as HTMLElement[];

    if (~elementPath.indexOf(openRef.current)) {
      //  clicked inside of popover
      return true;
    }

    const dropdown = elementPath.find(e => typeof e.getAttribute === 'function' &&
      e.getAttribute('data-anchor-id'));
    if (dropdown && isDropdownAnchorAChild(dropdown.getAttribute('data-anchor-id'))) {
      return true;
    }

    return false;
  }

  function isDropdownAnchorAChild(anchorId: string) {
    return document.querySelector(`#${id.current} #${anchorId}`) != null;
  }

  /**
   * Assigns `null` to `activePopover` if it is not attached to the DOM.
   */
  function evaluateActivePopover() {
    if (!activePopover) {
      return;
    }

    if (document.getElementById(activePopover.id) == null) {
      activePopover = null;
    }
  }

  //  For closing this popover if clicked outside of it
  React.useEffect(() => {
    let { body } = document
    if (open) {
      setHidden(false);
      firedOnHidden.current = false;
    }

    if (open && dismissOutside) {
      evaluateActivePopover();

      //  hide other already open popover
      if (activePopover && activePopover.id !== id.current) {
        activePopover.onClose()
      }

      activePopover = {
        id: id.current,
        onClose: onClose
      }

      body.addEventListener('click', dismissableListener = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (!openRef.current) {
          //  no longer open, 
          body.removeEventListener('click', dismissableListener, {
            capture: true
          })
          return
        }

        if (includeClickableElement != null) {
          if (e.target == includeClickableElement) {
            return true
          }
        }

        if (target.getAttribute?.('data-keep-popovers-open')) {
          return true;
        }

        if (isInsideThisElement(e)) {
          return;
        }

        e.stopPropagation()
        e.preventDefault()

        let c = onClose
        if (typeof c === "function") {
          c()
        }
      }, {
        capture: true
      })
    } else {
      body.removeEventListener('click', dismissableListener, {
        capture: true
      })
    }

    return () => { }
  }, [open]);

  return (
    <>
      <div className={cls([styles.backdrop, open ? styles.open : null])}></div>
      <div
        id={id.current}
        ref={e => {
          containerRef.current = e;
          if (open) {
            openRef.current = e;
          }
        }}
        className={cls([className, cls({
          [styles.popover]: true,
          [styles.open]: open
        })])}
        style={style}
        data-horizontal={horizontalPosition}
        data-vertical={verticalPosition}
        onMouseMove={e => {
          //  don't propagate to zoomable view
          e.stopPropagation()
        }}
        onTouchMove={e => {
          //  don't propagate to zoomable view
          e.stopPropagation()
        }}
        onClick={e => {
          preventDismissFlag = true
          e.stopPropagation()
        }}
        onTransitionEnd={e => {
          if (e.target != containerRef.current) {
            return;
          }

          if (!open && !hidden) {
            setHidden(true);
          }

          if (!open && !firedOnHidden.current) {
            firedOnHidden.current = true;
            onHidden?.();
          }
        }}>
        {
          !hidden ? (
            wrapChildren ? (
              <PopoverBody
                title={title}
                action={closeButton ? {
                  onClick: onClose
                } : null}>
                {children}
              </PopoverBody>
            ) : children
          ) : null
        }
        <div className={styles.notchContainer}>
          <div className={styles.notch}></div>
        </div>
      </div>
    </>
  )
}